import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { getPersonnel as getPersonnelSql } from '../../../services/migration/personnelDataService';
import {
  WmsPersonnelPermissions,
  WmsScreenType,
  wmsDefaultPersonnelPermissions,
  wmsPersonnelPermissionService,
} from '../services/wmsPersonnelPermissionService';
import useWmsSession from './useWmsSession';

interface WmsAccessContextValue {
  personnelId: string;
  personnelName: string;
  actionPermissions: WmsPersonnelPermissions;
  isAdminPreview?: boolean;
  responsiblePersonnelNames?: string[];
}

const WmsAccessContext = createContext<WmsAccessContextValue | null>(null);

interface WmsScreenAccessGateProps {
  screenType: WmsScreenType;
  screenId: string;
  children: React.ReactNode;
}

const WmsScreenAccessGate: React.FC<WmsScreenAccessGateProps> = ({ screenType, screenId, children }) => {
  const navigate = useNavigate();
  const { currentUser, userDetails } = useAuth();
  const { setPersonnelSession } = useWmsSession();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [ctxValue, setCtxValue] = useState<WmsAccessContextValue | null>(null);

  useEffect(() => {
    const run = async () => {
      if (!currentUser) {
        setLoading(false);
        return;
      }

      const companyId = userDetails?.companyId;
      if (!companyId) {
        setError('Sirket bilgisi bulunamadi. Lutfen tekrar giris yapin.');
        setLoading(false);
        return;
      }

      if (!screenId) {
        setError('Ekran kimligi (screenId) bulunamadi.');
        setLoading(false);
        return;
      }

      if (userDetails?.role === 'admin') {
        const adminName = `${userDetails.firstName || ''} ${userDetails.lastName || ''}`.trim() || currentUser.email || 'Admin';
        let actingPersonnelId = `admin-${currentUser.uid}`;
        let actingPersonnelName = adminName;
        let responsiblePersonnelNames: string[] = [];
        try {
          // Faz 3: personel listesi SQL'den; bu ekrana yetkili olanlar SQL izinlerinden (HybridFallback servis icinde).
          const personnel = await getPersonnelSql(companyId);
          const checked = await Promise.all(
            (personnel || []).map(async (p: any) => ({
              id: p.id,
              name: String(p?.name || '').trim() || 'Isimsiz Personel',
              hasAccess: await wmsPersonnelPermissionService.hasScreenAccess(companyId, p.id, screenId),
            }))
          );
          const responsiblePersonnel = checked
            .filter((item) => item.hasAccess)
            .map((item) => ({ id: item.id, name: item.name }))
            .sort((a, b) => a.name.localeCompare(b.name, 'tr'));

          responsiblePersonnelNames = responsiblePersonnel.map((item) => item.name);
          if (responsiblePersonnel.length > 0) {
            // Admin onizlemede ekrani sorumlu personel kimligiyle aciyoruz:
            // hem "Hosgeldin" ismi hem de is listeleri bu personele gore gelsin.
            actingPersonnelId = responsiblePersonnel[0].id;
            actingPersonnelName = responsiblePersonnel[0].name;
          }
        } catch {
          responsiblePersonnelNames = [];
        }

        const adminCtx: WmsAccessContextValue = {
          personnelId: actingPersonnelId,
          personnelName: actingPersonnelName,
          actionPermissions: wmsDefaultPersonnelPermissions,
          isAdminPreview: true,
          responsiblePersonnelNames,
        };
        setPersonnelSession({
          personnelId: adminCtx.personnelId,
          personnelName: adminCtx.personnelName,
          email: currentUser.email || '',
          screenType,
          screenId,
          actionPermissions: adminCtx.actionPermissions,
        });
        setCtxValue(adminCtx);
        setError(null);
        setLoading(false);
        return;
      }

      if (!currentUser.email) {
        setError('Kullanici e-posta bilgisi bulunamadi. Lutfen tekrar giris yapin.');
        setLoading(false);
        return;
      }

      try {
        const resolved = await wmsPersonnelPermissionService.resolvePersonnel(companyId, currentUser.email);
        if (!resolved) {
          setError('Sistem yoneticisi sizi personel olarak tanimlamamis.');
          setLoading(false);
          return;
        }

        const hasAccess = await wmsPersonnelPermissionService.hasScreenAccess(
          companyId,
          resolved.personnelId,
          screenId
        );

        if (!hasAccess) {
          setError('Bu ekrana erisim yetkiniz yok.');
          setLoading(false);
          return;
        }

        const actionPermissions = await wmsPersonnelPermissionService.getScreenActionPermissions(
          companyId,
          screenId,
          resolved.personnelId
        );

        setPersonnelSession({
          personnelId: resolved.personnelId,
          personnelName: resolved.personnelName,
          email: currentUser.email,
          screenType,
          screenId,
          actionPermissions,
        });

        setCtxValue({
          personnelId: resolved.personnelId,
          personnelName: resolved.personnelName,
          actionPermissions,
        });
        setError(null);
      } catch {
        setError('Yetki kontrolu yapilirken hata olustu. Lutfen tekrar deneyin.');
      } finally {
        setLoading(false);
      }
    };

    run();
  }, [
    currentUser,
    screenId,
    screenType,
    setPersonnelSession,
    userDetails?.companyId,
    userDetails?.firstName,
    userDetails?.lastName,
    userDetails?.role,
  ]);

  const providerValue = useMemo(() => ctxValue, [ctxValue]);

  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  if (loading) {
    return (
      <Box
        sx={{
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
          p: 2,
        }}
      >
        <Stack spacing={2} alignItems="center">
          <CircularProgress />
          <Typography color="text.secondary">Yetki kontrol ediliyor...</Typography>
        </Stack>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
          p: 2,
        }}
      >
        <Stack spacing={2} sx={{ maxWidth: 520, width: '100%' }}>
          <Alert severity="error">{error}</Alert>
          <Button variant="contained" onClick={() => navigate('/')}>
            Ana sayfaya don
          </Button>
        </Stack>
      </Box>
    );
  }

  if (!providerValue) return null;

  return (
    <WmsAccessContext.Provider value={providerValue}>
      {children}
    </WmsAccessContext.Provider>
  );
};

export const useWmsScreenAccess = () => useContext(WmsAccessContext);

export default WmsScreenAccessGate;

